import React, { useState, useEffect } from 'react';

// Third Party Libraries
import { useElements, useStripe, PaymentElement } from '@stripe/react-stripe-js';

// Hooks
import { useDispatch } from 'react-redux';

// Actions
import { addPaymentMethod, getAllPaymentMethods } from '~/redux/actions/stripe-actions.js';
import { getUserData } from '~/redux/actions/auth-actions.js';
import { failure, success } from '~/redux/actions/snackbar-actions.js';

// Utils
import { MESSAGES } from '~/utils/message.js';
import { getPaymentFailedAtHeader } from '~/utils/request';

//MUI Component
import { Box, CircularProgress } from '@mui/material';

// Styles
import './styles.scss';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

const PAYMENT_METHOD_REFRESH_ATTEMPTS = 3;
const PAYMENT_METHOD_REFRESH_DELAY = 700;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const PaymentMethod = (props) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [isPaymentReady, setIsPaymentReady] = useState(false);

  const {
    handleClose,
    open,
    handleExport,
    message,
    header,
    handleLiveModeSwitch,
    onSuccessMethod,
    onPaymentProcessing,
    className = '',
    bottomContent = null,
    disableForm = false,
    submitAction = null,
  } = props;

  useEffect(() => {
    if (open) {
      setIsPaymentReady(false);
    }
  }, [open]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!elements || !stripe) {
        return;
      }

      const { error: submitError } = await elements.submit();
      if (submitError?.message) {
        return;
      }

      setLoading(true);
      const { paymentMethod, ...response } = await stripe.createPaymentMethod({
        elements,
      });
      if (paymentMethod) {
        try {
          const attachResponse = await addPaymentMethod(paymentMethod);

          // The API server returns 200 even when the account is still locked after attachment.
          // Two distinct cases, distinguished by the x-payment-declined header:
          //
          //  x-payment-failed-at present + x-payment-declined: true
          //    → The new card was genuinely declined for every open invoice.
          //      Keep the form open so the user can enter a different card.
          //
          //  x-payment-failed-at present, no x-payment-declined
          //    → The backend's 30-second webhook poll timed out, but the card was charged
          //      and the webhook server is still processing. The background poller will
          //      auto-close the modal once confirmed. Signal the parent component so it
          //      can show a "processing" state while we wait.
          const paymentStillFailed = !!getPaymentFailedAtHeader(attachResponse?.headers);
          const cardDeclined = attachResponse?.headers?.['x-payment-declined'] === 'true';

          if (paymentStillFailed) {
            if (cardDeclined) {
              dispatch(failure(MESSAGES.ACCOUNT.WALLET.STRIPE.CARD_DECLINED_FOR_PAYMENT, ' '));
              // Leave the form open — user should try another card.
              setLoading(false);
              return;
            }

            // Account is still locked after attach — payment is collected on set-default,
            // not attach. In the recovery modal, proceed to set-default while showing
            // the processing state.
            if (typeof onSuccessMethod === 'function') {
              if (typeof onPaymentProcessing === 'function') {
                onPaymentProcessing();
              } else {
                handleClose();
              }
              await onSuccessMethod(paymentMethod.id);
              setLoading(false);
              return;
            }

            if (typeof onPaymentProcessing === 'function') {
              onPaymentProcessing();
            } else {
              handleClose();
            }
            setLoading(false);
            return;
          }

          dispatch(success(MESSAGES.ACCOUNT.WALLET.STRIPE.METHOD_ADDED, ' '));
          await dispatch(getUserData());

          for (let attempt = 1; attempt <= PAYMENT_METHOD_REFRESH_ATTEMPTS; attempt += 1) {
            const methods = await dispatch(getAllPaymentMethods());
            const hasNewPaymentMethod = Array.isArray(methods)
              ? methods.some((method) => method.id === paymentMethod.id)
              : false;

            if (hasNewPaymentMethod || attempt === PAYMENT_METHOD_REFRESH_ATTEMPTS) {
              break;
            }

            await delay(PAYMENT_METHOD_REFRESH_DELAY);
          }

          if (typeof handleLiveModeSwitch == 'function') handleLiveModeSwitch(true);
          handleClose();
          if (typeof onSuccessMethod == 'function') onSuccessMethod(paymentMethod.id);
        } catch (err) {
          if (typeof handleLiveModeSwitch == 'function') handleLiveModeSwitch(false);
          dispatch(
            failure(
              err?.response?.data?.message || MESSAGES.ACCOUNT.WALLET.STRIPE.METHOD_FAILED,
              ' '
            )
          );
        } finally {
          setLoading(false);
        }
      } else {
        dispatch(failure(response?.error?.message, ' '));
      }
    } catch (error) {
      if (typeof handleLiveModeSwitch == 'function') handleLiveModeSwitch(false);
      dispatch(failure(MESSAGES.ACCOUNT.WALLET.STRIPE.METHOD_FAILED, ' '));
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 3000);
    }
  };
  return (
    <OlcFormModal
      open={open}
      onClose={handleClose}
      title={MESSAGES.ACCOUNT.WALLET.STRIPE.ADD_PAYMENT_METHOD}
      className={`paymentModalWrapper ${className}`}
      footer={
        isPaymentReady ? (
          <>
            <OlcButton variant="secondary" onClick={handleClose}>
              {MESSAGES.ACCOUNT.WALLET.STRIPE.CANCEL}
            </OlcButton>
            <OlcButton
              variant="primary"
              disabled={submitAction ? false : loading && isPaymentReady}
              onClick={submitAction ? submitAction.onClick : handleSubmit}
            >
              {submitAction ? (
                submitAction.label
              ) : loading ? (
                <CircularProgress
                  sx={{
                    color: 'white',
                    width: '25px !important',
                    height: '25px !important',
                  }}
                />
              ) : (
                MESSAGES.ACCOUNT.WALLET.STRIPE.ADD_PAYMENT_METHOD
              )}
            </OlcButton>
          </>
        ) : null
      }
    >
      <Box
        className={`stripePayment ${isPaymentReady ? 'isReady' : 'isLoading'}`}
        // Greyed out (not unmounted) so entered card details survive and the layout doesn't jump.
        // Inline style because the stylesheet's `.stripePayment` rules outrank `sx`.
        style={disableForm ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
        aria-disabled={disableForm || undefined}
      >
        {!isPaymentReady && (
          <Box className="stripePaymentLoader">
            <CircularProgress sx={{ color: '#ed5c2f' }} />
          </Box>
        )}
        <Box className="stripePaymentElement">
          <PaymentElement
            options={{ readOnly: disableForm }}
            onReady={() => setIsPaymentReady(true)}
          />
        </Box>
      </Box>
      {isPaymentReady && bottomContent}
    </OlcFormModal>
  );
};

export default PaymentMethod;
